// path: frontend/src/hooks/useTaskSave.js
import { useCallback, useRef, useState } from "react";
import { DIALOGS } from "../utils/constants";
import { fmtDMY } from "../utils/date";
import { parseTagsInput } from "../utils/tags";
import {
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
} from "../api/queries/tasks";
import { useDataHelpers } from "./useDataHelpers";
import { useConfirm } from "../context/ConfirmContext";

const normPlanned = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const normDate = (v) => {
  if (v === "" || v === undefined) return null;
  return v;
};

// Функция для вычисления diff между формой и задачей из БД
function buildDiffPayload(existing, taskToSave) {
  if (!existing) return taskToSave;

  const diff = {};
  const norm = (v) => (v === undefined || v === null || v === "" ? null : v);
  const num = (v) => (norm(v) === null ? null : Number(v));

  const eq = (a, b) => {
    if (a === null && b === null) return true;
    if (a === null || b === null) return false;
    return String(a) === String(b);
  };

  if (!eq(norm(taskToSave.title), norm(existing.title)))
    diff.title = taskToSave.title;
  if (!eq(norm(taskToSave.desc), norm(existing.description)))
    diff.description = taskToSave.desc;
  if (!eq(norm(taskToSave.priority), norm(existing.priority)))
    diff.priority = taskToSave.priority;
  if (!eq(norm(taskToSave.assigneeId), norm(existing.assigneeId)))
    diff.assigneeId = taskToSave.assigneeId;
  if (num(taskToSave.plannedHours) !== num(existing.plannedHours))
    diff.plannedHours = taskToSave.plannedHours;

  if (!eq(norm(taskToSave.start), norm(existing.startDate)))
    diff.startDate = taskToSave.start;
  if (!eq(norm(taskToSave.deadline), norm(existing.deadline)))
    diff.deadline = taskToSave.deadline;
  if (Boolean(taskToSave.isHourly) !== Boolean(existing.isHourly))
    diff.isHourly = taskToSave.isHourly;
  if (!eq(norm(taskToSave.startTime), norm(existing.startTime)))
    diff.startTime = taskToSave.startTime;
  if (!eq(norm(taskToSave.endTime), norm(existing.endTime)))
    diff.endTime = taskToSave.endTime;
  if (!eq(norm(taskToSave.status), norm(existing.status)))
    diff.status = taskToSave.status;
  if (!eq(norm(taskToSave.projectId), norm(existing.projectId)))
    diff.projectId = taskToSave.projectId;
  if (!eq(norm(taskToSave.parentTaskId), norm(existing.parentTaskId)))
    diff.parentTaskId = taskToSave.parentTaskId;
  if (!eq(norm(taskToSave.dependencyId), norm(existing.dependencyId)))
    diff.dependencyId = taskToSave.dependencyId;
  if (!eq(norm(taskToSave.dependencyType), norm(existing.dependencyType)))
    diff.dependencyType = taskToSave.dependencyType;
  if (Boolean(taskToSave.isSummary) !== Boolean(existing.isSummary))
    diff.isSummary = taskToSave.isSummary;

  const tagsA = JSON.stringify(taskToSave.tags || []);
  const tagsB = JSON.stringify(existing.tags || []);
  if (tagsA !== tagsB) diff.tags = taskToSave.tags;

  return diff;
}

export function useTaskSave({
  existing,
  isNew,
  onSave,
  onDelete,
  ur,
  toast,
  subtasks,
  draftSubtasks,
  pendingTemplateSubtasks,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const createMutation = useCreateTaskMutation();
  const updateMutation = useUpdateTaskMutation();
  const deleteMutation = useDeleteTaskMutation();
  const { vacOverlap } = useDataHelpers();
  const { confirm } = useConfirm();

  const saveHandler = useCallback(
    async (vals) => {
      if (savingRef.current) return;
      if (!isNew && !existing?.id) {
        toast("Не удалось определить задачу для сохранения", "error");
        return;
      }
      savingRef.current = true;
      setIsSaving(true);
      try {
        const vacWarn =
          vals.assigneeId && vals.deadline
            ? vacOverlap(
                vals.assigneeId,
                vals.start || vals.deadline,
                vals.deadline,
              )
            : null;
        if (vacWarn) {
          const ok = await confirm(
            DIALOGS.vacationOverlap(fmtDMY(vacWarn.start), fmtDMY(vacWarn.end)),
          );
          if (!ok) return;
        }

        if (subtasks.length > 0 || draftSubtasks.length > 0)
          vals.isSummary = true;

        const tags = parseTagsInput(vals.tagsInput).valid;
        const taskToSave = {
          ...vals,
          tags,
          notes: existing?.notes,
          plannedHours: normPlanned(vals.plannedHours),
          creatorId: existing?.creatorId || ur.id,
        };
        delete taskToSave.tagsInput;

        // Строим payload: для новой задачи — всё, для существующей — только diff
        let payload = isNew
          ? { ...taskToSave }
          : buildDiffPayload(existing, taskToSave);

        delete payload.id;
        delete payload.creatorId;
        delete payload.history;
        delete payload.notes;
        delete payload.actualHours;

        if (payload.desc !== undefined) {
          payload.description = payload.desc;
          delete payload.desc;
        }
        if (payload.start !== undefined) {
          payload.startDate = normDate(payload.start);
          delete payload.start;
        }
        if (payload.deadline !== undefined) {
          payload.deadline = normDate(payload.deadline);
        }

        let needsConfirmation = false;
        let result;

        try {
          if (isNew) {
            result = await createMutation.mutateAsync({
              body: payload,
              needsConfirmation,
            });
          } else {
            // Если изменений нет, просто возвращаем текущую задачу
            if (Object.keys(payload).length === 0) {
              onSave(existing, false, {
                templateSubtasks: pendingTemplateSubtasks,
              });
              return;
            }
            result = await updateMutation.mutateAsync({
              id: existing.id,
              body: payload,
              needsConfirmation,
              version: existing.version,
            });
          }
        } catch (e) {
          if (e.status === 409 && e.details?.needsConfirmation) {
            const ok = await confirm({
              title: "Конфликт данных",
              message: e.message,
              confirmLabel: "Продолжить",
            });
            if (!ok) return;
            needsConfirmation = true;
            if (isNew) {
              result = await createMutation.mutateAsync({
                body: payload,
                needsConfirmation,
              });
            } else {
              result = await updateMutation.mutateAsync({
                id: existing.id,
                body: payload,
                needsConfirmation,
              });
            }
          } else {
            throw e;
          }
        }

        const needsApproval = Boolean(result.pendingApprovalId);
        toast(
          needsApproval
            ? `Задача «${result.title}» отправлена на согласование назначения`
            : `Задача «${result.title}» сохранена`,
          needsApproval ? "info" : "success",
        );
        onSave(result, isNew, { templateSubtasks: pendingTemplateSubtasks });
      } catch (err) {
        console.error(err);
        toast(err.message || "Ошибка сохранения задачи", "error");
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [
      existing,
      isNew,
      vacOverlap,
      onSave,
      ur,
      subtasks,
      draftSubtasks,
      pendingTemplateSubtasks,
      confirm,
      toast,
      createMutation,
      updateMutation,
    ],
  );

  const saveAsDraftHandler = useCallback(
    async (vals) => {
      if (savingRef.current) return;
      if (!isNew && !existing?.id) {
        toast("Не удалось определить задачу для сохранения", "error");
        return;
      }
      savingRef.current = true;
      setIsSaving(true);
      try {
        const tags = parseTagsInput(vals.tagsInput).valid;
        const taskToSave = {
          ...vals,
          tags,
          status: "draft",
          pendingApprovalId: null,
          plannedHours: normPlanned(vals.plannedHours),
        };
        delete taskToSave.tagsInput;

        let payload = isNew
          ? { ...taskToSave }
          : buildDiffPayload(existing, taskToSave);

        delete payload.id;
        delete payload.creatorId;
        delete payload.history;
        delete payload.notes;
        delete payload.actualHours;

        if (payload.desc !== undefined) {
          payload.description = payload.desc;
          delete payload.desc;
        }
        if (payload.start !== undefined) {
          payload.startDate = normDate(payload.start);
          delete payload.start;
        }

        let result;
        if (isNew) {
          result = await createMutation.mutateAsync({ body: payload });
        } else {
          if (Object.keys(payload).length === 0) {
            onSave(existing, false, {});
            return;
          }
          result = await updateMutation.mutateAsync({
            id: existing.id,
            body: payload,
            version: existing.version,
          });
        }
        toast(`Черновик «${result.title}» сохранен`, "success");
        onSave(result, isNew, {});
      } catch (err) {
        toast(err.message || "Ошибка сохранения черновика", "error");
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [existing, isNew, onSave, createMutation, updateMutation, toast],
  );

  const deleteHandler = useCallback(async () => {
    if (savingRef.current) return;
    if (!existing?.id) {
      toast("Задача ещё не сохранена - нечего удалять", "warning");
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    try {
      const ok = await confirm(DIALOGS.deleteTask(existing.title));
      if (!ok) return;
      await deleteMutation.mutateAsync({ id: existing.id });
      toast("Задача удалена", "success");
      onDelete(existing.id);
    } catch (err) {
      toast(err.message || "Ошибка удаления задачи", "error");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [existing, onDelete, confirm, deleteMutation, toast]);

  return { saveHandler, saveAsDraftHandler, deleteHandler, isSaving };
}
